"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CalendarRange, ChartLine as LineChart, ShieldAlert, ArrowUpRight, Sun, CloudRain, Sparkles } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const challenges = [{
  id: 1,
  title: "Seasonality & Weekend Compression",
  subtitle: "Demand management",
  iconName: "CalendarRange",
  description: "Manage sharp weekend influxes from Kolkata and West Bengal drive-in travelers against midweek and off-season dips. We build dynamic minimum length-of-stay controls and real-time pace pricing so your property never relies on panic discounting.",
  metricLabel: "Strategy focus",
  metricValue: "Pace-based pricing & weekend minimum stay controls"
}, {
  id: 2,
  title: "Aggressive Comp-Set Undercutting",
  subtitle: "Real-time rate intelligence",
  iconName: "LineChart",
  description: "Track rapid rate fluctuations across MakeMyTrip, Goibibo, Agoda, and Booking.com. Automated competitive alerts and yield adjustments protect your RevPAR while capturing high-intent booking demand ahead of local market price cuts.",
  metricLabel: "Strategy focus",
  metricValue: "Intraday OTA tracking & rule-based rate fencing"
}, {
  id: 3,
  title: "The OTA Commission & Parity Trap",
  subtitle: "Channel distribution",
  iconName: "ShieldAlert",
  description: "Eliminate unauthorized wholesale discounting and recover margins lost to high OTA commissions. We enforce strict rate parity across meta-search and drive direct guest reservations through high-converting booking incentives.",
  metricLabel: "Strategy focus",
  metricValue: "Direct booking engine optimization & parity defense"
}];
const seasonalityPhases = [{
  phase: "Peak Season",
  months: "October – February & Durga Puja",
  icon: Sparkles,
  badgeText: "High ADR Yield",
  desc: "Premium weekend and holiday surge periods. Strategy focuses on strict minimum length of stay (MLOS), early bird yield ladders, and maximizing ADR without risking occupancy drops."
}, {
  phase: "Shoulder & Weekend Rush",
  months: "March – June",
  icon: Sun,
  badgeText: "Dynamic Weekday Balancing",
  desc: "High volume of weekend drive-in getaways with lighter midweek occupancies. Dynamic rate flexing and packaged family stays capture extended bookings across New Digha and Old Digha."
}, {
  phase: "Monsoon & Low Season",
  months: "July – September",
  icon: CloudRain,
  badgeText: "Occupancy Protection",
  desc: "Focus on corporate retreats, government travel, and targeted local leisure packages with fenced promotions to maintain steady baseline cash flow and market visibility."
}];
export default function Landscape() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="hospitalitylandscapedigha" data-nav="light" className="relative bg-background py-20 md:py-28 text-foreground">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Section Header */}
        <div className="mx-auto max-w-3xl text-center mb-16 md:mb-20">
          <Badge variant="outline" className="mb-4 border-secondary/40 text-secondary-foreground">
            Market Dynamics & Yield Protection
          </Badge>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-foreground">
            Navigating the hospitality landscape of Digha
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Digha presents a dynamic coastal micro-market characterized by heavy weekend leisure compression, holiday surges, and intense local hotel competition. Sunil Singh structures disciplined revenue models designed specifically for Digha resorts, sea-facing hotels, and boutique properties.
          </p>
        </div>

        {/* 3-Column Challenge Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {challenges.map((item, index) => <motion.div key={item.id} data-index={index} variants={cardVariants} className="h-full flex">
              <Card className="flex flex-col justify-between w-full border border-border bg-card shadow-sm hover:shadow-md transition-[border-color,box-shadow] duration-300">
                <CardHeader className="gap-3">
                  <div className="flex items-center justify-between">
                    <div className="inline-flex size-12 items-center justify-center rounded-md bg-secondary/10 text-secondary-foreground">
                      {item.iconName === "CalendarRange" && <CalendarRange className="size-6 text-chart-2" />}
                      {item.iconName === "LineChart" && <LineChart className="size-6 text-chart-2" />}
                      {item.iconName === "ShieldAlert" && <ShieldAlert className="size-6 text-chart-2" />}
                    </div>
                    <span className="text-xs font-mono font-medium text-muted-foreground tracking-wider uppercase">
                      0{index + 1}
                    </span>
                  </div>
                  <div>
                    <span className="text-xs font-semibold uppercase tracking-wider text-secondary">
                      {item.subtitle}
                    </span>
                    <CardTitle className="font-serif text-xl sm:text-2xl font-bold mt-1.5 text-foreground leading-snug">
                      {item.title}
                    </CardTitle>
                  </div>
                </CardHeader>

                <CardContent className="space-y-6 pt-2">
                  <CardDescription className="text-sm leading-relaxed text-muted-foreground">
                    {item.description}
                  </CardDescription>

                  <div className="rounded-md border border-border/60 bg-muted/40 p-3.5 text-xs">
                    <p className="font-semibold text-foreground mb-1">
                      {item.metricLabel}
                    </p>
                    <p className="text-muted-foreground">
                      {item.metricValue}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>)}
        </motion.div>

        {/* Seasonality Breakdown Block */}
        <div className="mt-14 md:mt-20 rounded-xl border border-border bg-card p-6 md:p-8 lg:p-10 shadow-sm">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
            <div>
              <span className="text-xs font-mono font-semibold uppercase tracking-widest text-secondary">
                Strategic Revenue Calendar
              </span>
              <h3 className="font-serif text-2xl md:text-3xl font-bold mt-1 text-foreground">
                Digha Seasonality & Demand Cycles
              </h3>
            </div>
            <p className="text-sm text-muted-foreground max-w-md">
              How we pace pricing, room allocation, and minimum night requirements across Digha&apos;s distinct market seasons.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {seasonalityPhases.map((phase, idx) => {
            const IconComp = phase.icon;
            return <div key={phase.phase} data-index={idx} className="rounded-lg border border-border/70 bg-muted/20 p-5 flex flex-col justify-between">
                  <div>
                    <div className="flex items-center justify-between gap-2 mb-3">
                      <div className="inline-flex size-9 items-center justify-center rounded-md bg-secondary/15 text-secondary">
                        <IconComp className="size-5" />
                      </div>
                      <Badge variant="secondary" className="text-[11px] font-medium">
                        {phase.badgeText}
                      </Badge>
                    </div>
                    <h4 className="font-semibold text-base text-foreground">{phase.phase}</h4>
                    <p className="text-xs font-mono text-secondary mt-0.5 mb-2.5">{phase.months}</p>
                    <p className="text-xs text-muted-foreground leading-relaxed">{phase.desc}</p>
                  </div>
                </div>;
          })}
          </div>
        </div>

        {/* Section Bottom CTA / Footnote */}
        <div className="mt-12 md:mt-16 pt-8 border-t border-border/70 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-sm text-muted-foreground text-center sm:text-left">
            Need a tailored revenue assessment for your Digha hotel or boutique stay?
          </p>
          <Button asChild variant="default" className="bg-primary text-primary-foreground hover:bg-primary/90">
            <Link to="#" className="inline-flex items-center gap-2">
              <span>Book a Revenue Audit</span>
              <ArrowUpRight className="size-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}